/**
 * usePoolPriorities — the ranked members of an item pool, derived from the
 * items store's data rather than from its stable function references.
 *
 * Finding F11 (docs/9-exploration/MOSSO-DEMO-FINDINGS-2026-08-25.md): the pool
 * editor memoized its ranked list on `[activeList, getItemById]`. `getItemById`
 * never changes identity, so when pools resolved before items every lookup
 * missed and the memo froze an empty list — the pool rendered empty until the
 * user switched pools and back.
 *
 * Two things fix it, and both live here so no caller can reintroduce either:
 *   1. Depend on the items array, so the list recomputes when items land.
 *   2. Report `isPending` separately, so the zero/one/few/many `zero` branch
 *      means truly zero and never "not loaded yet".
 */

import { useMemo } from 'react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { getPrioritiesFromList } from '../../lib/priorityContext';
import type { FocusList, Priority } from '../../types/prioritization';

export interface PoolPriorities {
  priorities: Priority[];
  /** The pool has members, but their items have not arrived yet. */
  isPending: boolean;
}

export function usePoolPriorities(pool: FocusList | null | undefined): PoolPriorities {
  const items = useItemsStore((state) => state.items);
  const isLoading = useItemsStore((state) => state.syncState.isLoading);

  const priorities = useMemo(() => {
    if (!pool) return [];
    const byId = new Map(items.map((item) => [item.id, item]));
    return getPrioritiesFromList(pool, (id) => byId.get(id));
  }, [pool, items]);

  return {
    priorities,
    isPending: isLoading && priorities.length === 0 && (pool?.priorityIds.length ?? 0) > 0,
  };
}
